import {
  Col,
  Row,
  Space,
  Table,
  Card,
  Button,
  message,
  Spin,
  Select,
  Flex,
  Input,
  notification,
  Modal,
  Empty,
  Typography,
  List,
  Divider,
  Tag,
  Checkbox,
} from "antd";
import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import {
  PlusOutlined,
  ReloadOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import MotionHoc from "../../../utils/MotionHoc";
import { Link } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import CreateAdvanePayment from "./modal/CreateAdvanePayment";
import {
  HEADERS_EXPORT_BEN,
  POST_CHECK_BILL,
  POST_MAIN_WITHDRAW,
} from "../../API/apiUrls";
import { ThaiRangePicker } from "../../../hook/ThaiDatePicker";
import LoadCompanies from "../../../hook/LoadCompanies";

// ✅ Constants
const MAX_SELECTION = 10;
const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = ["10", "20", "50", "100"];
const COMPANY_GROUPS = {
  1: [1, 4],
  4: [1, 4],
  2: [2, 5],
  5: [2, 5],
  3: [3],
};

const Main = () => {
  const userCompany = localStorage.getItem("COMPANY_ID");
  const userId = parseInt(localStorage.getItem("USER_ID"));
  const abortControllerRef = useRef(null);
  const { Text } = Typography;
  const { companiesOptions: allCompaniesOptions, loading: loadingCompanies } =
    LoadCompanies();

  // ✅ Default company based on user
  const defaultCompany = useMemo(() => {
    return userCompany === "3" ? 3 : 1;
  }, [userCompany]);

  // States
  const [isModalCreateAdvanePayment, setIsModalCreateAdvanePayment] =
    useState(false);
  const [arrayTable, setArrayTable] = useState([]);
  const [dataArr, setDataArr] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataModal, setDataModal] = useState([]);
  const [companieSelect, setCompanieSelect] = useState(defaultCompany);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [checkBillData, setCheckBillData] = useState([]);
  const [isBackdated, setIsBackdated] = useState(false);

  // ✅ โหลดข้อมูลหลัก - ใช้ useRef สำหรับ AbortController
  const loadMain = useCallback(async () => {
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);

    try {
      const res = await axios.post(
        POST_MAIN_WITHDRAW,
        { userId, currentStatus: 4 },
        {
          headers: HEADERS_EXPORT_BEN,
          signal: abortControllerRef.current.signal,
        },
      );

      if (res.data?.length > 0) {
        setDataArr(res.data);
        setArrayTable(res.data);
      } else {
        setDataArr([]);
        setArrayTable([]);
        message.info("ไม่มีข้อมูล");
      }
    } catch (error) {
      if (error.name === "CanceledError") return;

      console.error("❌ Error loading data:", error);
      message.error(error.response?.data?.message || "ไม่สามารถโหลดข้อมูลได้");
      setDataArr([]);
      setArrayTable([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // ✅ Cleanup on unmount
  useEffect(() => {
    loadMain();

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [loadMain]);

  // ✅ Check bill - เพิ่ม async/await handling
  const loadCheckBill = useCallback(async () => {
    setLoading(true);
    const payload = { companyId: companieSelect, userId };
    try {
      const response = await axios.post(POST_CHECK_BILL, payload, {
        headers: HEADERS_EXPORT_BEN,
      });

      if (response.data && Array.isArray(response.data)) {
        return response.data;
      }

      message.warning("ไม่พบข้อมูล");
      return null;
    } catch (error) {
      console.error("Error loading data:", error);
      message.error(`เกิดข้อผิดพลาด: ${error.message}`);
      return null;
    } finally {
      setLoading(false);
    }
  }, [companieSelect, userId]);

  // ✅ Companies Options
  const companiesOptions = useMemo(() => {
    if (!allCompaniesOptions?.length) return [];
    return allCompaniesOptions.filter((item) => item.value <= 3);
  }, [allCompaniesOptions]);

  // ✅ กรองข้อมูลตาม filters
  const filteredData = useMemo(() => {
    let result = [...dataArr];

    // 0. กรองตามสถานะ (ข้ามเมื่อเลือก "เบิกย้อนหลัง")
    if (!isBackdated) {
      result = result.filter(
        (item) =>
          (item.currentStatus === 7 || item.currentStatus === 4) &&
          !item.fee_payment_status,
      );
    }

    // 1. กรองตาม Company (แก้ไขจาก includes เป็น ===)
    const allowedCompanies = COMPANY_GROUPS[companieSelect] ?? [companieSelect];
    result = result.filter((item) =>
      allowedCompanies.includes(item.COMPANY_ID),
    );

    // 2. กรองตาม Search Text
    const searchTrimmed = searchText.trim().toLowerCase();
    if (searchTrimmed) {
      result = result.filter((item) => {
        const contno = item.CONTNO?.toLowerCase() || "";
        const provincialCourt = item.provincialCourt || "";
        return (
          contno.includes(searchTrimmed) ||
          provincialCourt.includes(searchTrimmed)
        );
      });
    }

    // 3. กรองตาม Date Range
    if (dateRange?.length === 2) {
      const [startDate, endDate] = dateRange;
      const start = dayjs(startDate).startOf("day");
      const end = dayjs(endDate).endOf("day");

      result = result.filter((item) => {
        if (!item.dateOfPlaint) return false;
        const itemDate = dayjs(item.dateOfPlaint);
        return (
          (itemDate.isAfter(start) || itemDate.isSame(start, "day")) &&
          (itemDate.isBefore(end) || itemDate.isSame(end, "day"))
        );
      });
    }

    return result;
  }, [dataArr, isBackdated, companieSelect, searchText, dateRange]);

  // ✅ Update arrayTable เมื่อ filteredData เปลี่ยน
  useEffect(() => {
    setArrayTable(filteredData);
  }, [filteredData]);

  // ✅ Reset selection helper
  const resetSelection = useCallback(() => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
    setDataModal([]);
  }, []);

  // ✅ Handlers
  const handleCompanyChange = useCallback(
    (value) => {
      setCompanieSelect(value);
      resetSelection();
    },
    [resetSelection],
  );

  const handleSearch = useCallback((e) => {
    setSearchText(e.target.value);
  }, []);

  const handleDateRangeChange = useCallback((dates) => {
    setDateRange(dates);
  }, []);

  const handleSelectChange = useCallback((keys, rows) => {
    setSelectedRowKeys(keys);
    setSelectedRows(rows);
    setDataModal(rows);
  }, []);

  const handleCreateAdvancePayment = useCallback(async () => {
    if (selectedRowKeys.length === 0) {
      message.warning("กรุณาเลือกสัญญาอย่างน้อย 1 รายการ");
      return;
    }
    if (selectedRowKeys.length > MAX_SELECTION) {
      message.warning(`สามารถเลือกได้สูงสุด ${MAX_SELECTION} รายการ`);
      return;
    }

    const dataCheck = await loadCheckBill();
    console.log(dataCheck);

    if (!dataCheck || dataCheck.length === 0) {
      setIsModalCreateAdvanePayment(true);
      return;
    }
    const unclearedItems = dataCheck.filter(
      (item) => item.advaneType === 1 && item.clearStatus !== 1,
    );

    if (unclearedItems.length === 0) {
      setIsModalCreateAdvanePayment(true);
    } else {
      notification.error({
        message: "ยังไม่เคลียร์รายการที่เบิก !",
        description: (
          <div>
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              {unclearedItems.map((item, index) => (
                <li key={index}>{item.TMBILL}</li>
              ))}
            </ul>
          </div>
        ),
        duration: 5,
      });
    }
  }, [selectedRowKeys.length, loadCheckBill]);

  const handleResetFilters = useCallback(() => {
    setSearchText("");
    setDateRange(null);
    setCompanieSelect(defaultCompany);
    resetSelection();
    message.info("ล้างตัวกรองสำเร็จ");
  }, [defaultCompany, resetSelection]);

  const handleClearSelection = useCallback(() => {
    resetSelection();
    message.info("ยกเลิกการเลือกแล้ว");
  }, [resetSelection]);

  const handleModalCallback = useCallback(() => {
    resetSelection();
    loadMain();
  }, [loadMain]);

  // ✅ Row key generator
  const getRowKey = useCallback(
    (record) => `${record.loanId}_${record.lawsuitId}`,
    [],
  );

  // ✅ Check if filters are active
  const hasActiveFilters = useMemo(() => {
    return searchText || dateRange || companieSelect !== defaultCompany;
  }, [searchText, dateRange, companieSelect, defaultCompany]);

  const handleOpenCheckList = async () => {
    setIsModalOpen(true);
    const dataCheck = await loadCheckBill();
    setCheckBillData(dataCheck);
    console.log("dataCheck", dataCheck);
  };

  const CHECK_BILL_STATUS_HEX = {
    green: "#52c41a",
    orange: "#fa8c16",
    blue: "#1890ff",
    default: "#d9d9d9",
  };

  const getCheckBillStatus = (approvalStatus, clear_send, clear_date) => {
    if (clear_date) {
      return { color: "green", text: "อนุมัติเคลียร์แล้ว" };
    }
    if (clear_send) {
      return { color: "orange", text: "รออนุมัติเคลียร์" };
    }
    if (approvalStatus === 1) {
      return { color: "blue", text: "แม่ทมอนุมัติแล้ว" };
    }
    return { color: "default", text: "ทนายยื่นเบิก" };
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  // ✅ Columns
  const columns = useMemo(
    () => [
      {
        title: "ลำดับ",
        key: "index",
        align: "center",
        width: 40,
        render: (_, __, index) => index + 1,
      },
      {
        title: "เลขที่สัญญา",
        dataIndex: "CONTNO",
        key: "CONTNO",
        align: "center",
        width: 60,
        render: (text) => (
          <Link style={{ fontWeight: 600 }}>{text || "-"}</Link>
        ),
      },
      {
        title: "ศาล",
        dataIndex: "provincialCourt",
        key: "provincialCourt",
        align: "center",
        width: 80,
        ellipsis: { showTitle: false },
      },
      {
        title: "วันยื่นฟ้อง",
        dataIndex: "dateOfPlaint",
        key: "dateOfPlaint",
        align: "center",
        width: 60,
        sorter: (a, b) => {
          const dateA = a.dateOfPlaint ? new Date(a.dateOfPlaint) : new Date(0);
          const dateB = b.dateOfPlaint ? new Date(b.dateOfPlaint) : new Date(0);
          return dateB - dateA;
        },
        render: (date) =>
          date ? dayjs(date).add(543, "year").format("D MMM YY") : "-",
      },
      {
        title: "หมายเหตุ",
        dataIndex: "mark",
        key: "mark",
        width: 150,
        ellipsis: { showTitle: false },
      },
    ],
    [],
  );

  // ✅ Row selection config
  const rowSelection = useMemo(
    () => ({
      selectedRowKeys,
      onChange: handleSelectChange,
      preserveSelectedRowKeys: true,
      getCheckboxProps: (record) => ({
        disabled:
          selectedRowKeys.length >= MAX_SELECTION &&
          !selectedRowKeys.includes(getRowKey(record)),
      }),
    }),
    [selectedRowKeys, handleSelectChange, getRowKey],
  );

  // ✅ Pagination config
  const pagination = useMemo(
    () => ({
      defaultPageSize: DEFAULT_PAGE_SIZE,
      showSizeChanger: true,
      showTotal: (total, range) =>
        `แสดง ${range[0]}-${range[1]} จาก ${total} รายการ`,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
    }),
    [],
  );

  return (
    <>
      <Card>
        <Spin spinning={loading} size="large" tip="กำลังโหลดข้อมูล...">
          <Row gutter={[16, 16]}>
            {/* Header Row */}
            <Col span={24}>
              <Flex justify="space-between" align="center" wrap="wrap" gap={16}>
                <Space size="middle">
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    size="large"
                    onClick={handleCreateAdvancePayment}
                    disabled={
                      selectedRowKeys.length === 0 ||
                      selectedRowKeys.length > MAX_SELECTION
                    }
                  >
                    สร้างรายการ
                    {selectedRowKeys.length > 0 &&
                      ` (${selectedRowKeys.length})`}
                  </Button>
                  {selectedRowKeys.length > 0 && (
                    <Button size="large" onClick={handleClearSelection}>
                      ยกเลิกการเลือก
                    </Button>
                  )}
                  <Checkbox
                    checked={isBackdated}
                    onChange={(e) => {
                      setIsBackdated(e.target.checked);
                      resetSelection();
                    }}
                  >
                    เบิกย้อนหลัง
                  </Checkbox>
                </Space>
                <Space size="middle">
                  <Button
                    color="green"
                    variant="outlined"
                    size="large"
                    onClick={handleOpenCheckList}
                  >
                    รายการรออนุมัติ
                  </Button>
                  <Select
                    placeholder="เลือกบริษัท"
                    showSearch
                    optionFilterProp="label"
                    options={companiesOptions}
                    onChange={handleCompanyChange}
                    value={companieSelect}
                    style={{ minWidth: 200 }}
                    size="large"
                    loading={loadingCompanies}
                    popupMatchSelectWidth={false}
                  />
                </Space>
              </Flex>
            </Col>

            {/* Filter Row */}
            <Col span={24}>
              <Flex justify="space-between" align="center" wrap="wrap" gap={16}>
                <Space size="middle">
                  <ThaiRangePicker
                    size="large"
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    placeholder={["วันที่เริ่มต้น", "วันที่สิ้นสุด"]}
                    style={{ width: 300 }}
                  />
                  <Button
                    icon={<ReloadOutlined />}
                    size="large"
                    onClick={handleResetFilters}
                  >
                    ล้างตัวกรอง
                  </Button>
                </Space>

                <Input
                  placeholder="ค้นหาเลขสัญญา, ศาล"
                  onChange={handleSearch}
                  value={searchText}
                  style={{ width: 280 }}
                  allowClear
                  size="small"
                  suffix={<span style={{ marginRight: 5 }}>🔍</span>}
                />
              </Flex>
            </Col>

            {/* Summary Row */}
            {hasActiveFilters && (
              <Col span={24}>
                <Card size="small" style={{ background: "#f0f5ff" }}>
                  <Space split="|" size="large">
                    <span>
                      <strong>ผลการค้นหา:</strong> {arrayTable.length} รายการ
                    </span>
                    {searchText && (
                      <span>
                        <strong>ค้นหา:</strong> "{searchText}"
                      </span>
                    )}
                    {dateRange && (
                      <span>
                        <strong>ช่วงวันที่:</strong>{" "}
                        {dayjs(dateRange[0])
                          .add(543, "year")
                          .format("D MMM YY")}{" "}
                        -{" "}
                        {dayjs(dateRange[1])
                          .add(543, "year")
                          .format("D MMM YY")}
                      </span>
                    )}
                  </Space>
                </Card>
              </Col>
            )}

            {/* Table */}
            <Col span={24}>
              <Table
                size="small"
                columns={columns}
                dataSource={arrayTable}
                rowKey={getRowKey}
                scroll={{ x: 1400 }}
                loading={loading}
                pagination={pagination}
                rowSelection={rowSelection}
                footer={() => (
                  <Flex
                    justify="space-between"
                    align="center"
                    style={{ fontWeight: 500 }}
                  >
                    <span
                      style={{
                        color: selectedRowKeys.length > 0 ? "#1890ff" : "#999",
                      }}
                    >
                      {selectedRowKeys.length > 0
                        ? `✓ เลือกแล้ว ${selectedRowKeys.length} สัญญา`
                        : "ยังไม่ได้เลือกสัญญา"}
                    </span>
                    <span>จำนวนสัญญาทั้งหมด: {arrayTable.length} รายการ</span>
                  </Flex>
                )}
              />
            </Col>
          </Row>
        </Spin>
      </Card>
      <Modal
        title={
          <Flex align="center" gap={8}>
            <ClockCircleOutlined style={{ color: "#1890ff" }} />
            <span>รายการรออนุมัติเบิก</span>
            {checkBillData.length > 0 && (
              <Tag color="blue" style={{ marginLeft: 2 }}>
                {checkBillData.length} รายการ
              </Tag>
            )}
          </Flex>
        }
        open={isModalOpen}
        onCancel={handleCancel}
        footer={[
          <Button key="close" onClick={handleCancel}>
            ปิด
          </Button>,
        ]}
        width={640}
        centered
        styles={{ body: { maxHeight: "65vh", overflowY: "auto" } }}
      >
        <Spin spinning={loading}>
          {checkBillData.length > 0 ? (
            <>
              <List
                dataSource={checkBillData}
                split={false}
                renderItem={(item) => {
                  const status = getCheckBillStatus(
                    item.approvalStatus,
                    item.clear_send,
                    item.clear_date,
                  );
                  return (
                    <Card
                      size="small"
                      style={{
                        marginBottom: 10,
                        borderRadius: 8,
                        borderInlineStart: `3px solid ${CHECK_BILL_STATUS_HEX[status.color]}`,
                      }}
                      styles={{ body: { padding: "10px 14px" } }}
                    >
                      <Flex justify="space-between" align="flex-start" gap={12}>
                        <div>
                          <Flex align="center" gap={8}>
                            <FileTextOutlined style={{ color: "#1890ff" }} />
                            <Text strong copyable>
                              {item.TMBILL}
                            </Text>
                            {status.color === "default" ? (
                              <Tag>{status.text}</Tag>
                            ) : (
                              <Tag color={status.color}>{status.text}</Tag>
                            )}
                          </Flex>
                          <Text
                            type="secondary"
                            style={{
                              fontSize: 12,
                              marginTop: 4,
                              display: "block",
                            }}
                          >
                            {dayjs(item.TMBILL_DT)
                              .add(543, "year")
                              .format("D MMM YYYY")}
                            {" · "}
                            {allCompaniesOptions.find(
                              (allCom) => allCom.value === item.COMPANY_ID,
                            )?.label || "-"}
                          </Text>
                        </div>
                        <Flex vertical align="flex-end" gap={4}>
                          <Tag
                            color={
                              item.advaneType === 1 ? "orange" : "geekblue"
                            }
                            style={{ marginInlineEnd: 0 }}
                          >
                            {item.advaneType === 2
                              ? "เบิกวางคำบังคับ"
                              : item.advaneType === 1
                                ? "เบิกฟ้อง"
                                : null}
                          </Tag>
                          <Text
                            strong
                            style={{ color: "#1890ff", fontSize: 16 }}
                          >
                            {Number(item.payamt).toLocaleString("th-TH")} บาท
                          </Text>
                        </Flex>
                      </Flex>
                    </Card>
                  );
                }}
              />
              <Divider style={{ margin: "12px 0" }} />
              <Flex justify="space-between" align="center">
                <Text type="secondary">ยอดรวมทั้งหมด</Text>
                <Text strong style={{ fontSize: 18, color: "#1890ff" }}>
                  {checkBillData
                    .reduce((sum, item) => sum + Number(item.payamt), 0)
                    .toLocaleString("th-TH")}{" "}
                  บาท
                </Text>
              </Flex>
            </>
          ) : (
            <Empty
              description="ไม่มีรายการรออนุมัติเบิก"
              style={{ padding: "40px 0" }}
            />
          )}
        </Spin>
      </Modal>

      {isModalCreateAdvanePayment && (
        <CreateAdvanePayment
          open={isModalCreateAdvanePayment}
          close={setIsModalCreateAdvanePayment}
          dataDefault={dataModal}
          funcUpdateStatus={handleModalCallback}
          company={companieSelect}
        />
      )}
    </>
  );
};

const WithdrawAdvancePayment = MotionHoc(Main);
export default WithdrawAdvancePayment;
